/**
 * PromotionSelector
 *
 * Component for selecting promotions to include in bulk content generation.
 * Shows only promotions that overlap with the planning date range.
 */

import React from 'react';
import {
    Tag,
    Check,
    Calendar,
    Percent,
    DollarSign,
    Gift,
    Package,
    Truck,
    AlertCircle,
    Info,
    Infinity
} from 'lucide-react';
import {
    getPromotionStatus,
    formatDiscountValue
} from '../../services/promotionService';

// Icons for promotion types
const PROMOTION_TYPE_ICONS = {
    percentage: Percent,
    fixed: DollarSign,
    bogo: Gift,
    bundle: Package,
    free_shipping: Truck
};

const PromotionSelector = ({
    startDate,
    endDate,
    promotions = [],
    selectedPromotions = [],
    onPromotionsChange
}) => {
    // Filter to promotions that overlap with the date range (ongoing promotions always included)
    const availablePromotions = React.useMemo(() => {
        if (!promotions.length) return [];

        // If no dates selected, only show ongoing promotions
        if (!startDate || !endDate) {
            return promotions.filter(promo => promo.isActive && promo.isOngoing);
        }

        const start = new Date(startDate);
        const end = new Date(endDate);

        return promotions.filter(promo => {
            if (!promo.isActive) return false;

            // Ongoing promotions are always included
            if (promo.isOngoing) return true;

            const promoStart = new Date(promo.startDate);
            const promoEnd = new Date(promo.endDate);

            // Check for overlap
            return promoStart <= end && promoEnd >= start;
        });
    }, [startDate, endDate, promotions]);

    const handleTogglePromotion = (promotion) => {
        const isSelected = selectedPromotions.some(p => p.id === promotion.id);

        if (isSelected) {
            onPromotionsChange(selectedPromotions.filter(p => p.id !== promotion.id));
        } else {
            onPromotionsChange([...selectedPromotions, promotion]);
        }
    };

    const formatDateRange = (promotion) => {
        if (promotion.isOngoing) {
            return 'Ongoing';
        }

        const start = new Date(promotion.startDate);
        const end = new Date(promotion.endDate);
        const options = { month: 'short', day: 'numeric' };

        return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
    };

    // Calculate overlap days for each promotion
    const getOverlapInfo = (promotion) => {
        // Ongoing promotions cover all days
        if (promotion.isOngoing) {
            if (!startDate || !endDate) {
                return { overlapDays: null, isOngoing: true };
            }
            const planStart = new Date(startDate);
            const planEnd = new Date(endDate);
            const totalDays = Math.ceil((planEnd - planStart) / (1000 * 60 * 60 * 24)) + 1;
            return {
                overlapDays: totalDays,
                isOngoing: true
            };
        }

        const planStart = new Date(startDate);
        const planEnd = new Date(endDate);
        const promoStart = new Date(promotion.startDate);
        const promoEnd = new Date(promotion.endDate);

        const overlapStart = new Date(Math.max(planStart, promoStart));
        const overlapEnd = new Date(Math.min(planEnd, promoEnd));
        const overlapDays = Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24)) + 1;

        return {
            overlapDays,
            startsBeforePlan: promoStart < planStart,
            endsAfterPlan: promoEnd > planEnd,
            isOngoing: false
        };
    };

    return (
        <div className="card" style={{ padding: 'var(--space-4)' }}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-3)'
            }}>
                <Tag size={20} style={{ color: 'var(--color-primary)' }} />
                <h4 style={{ margin: 0 }}>Active Promotions</h4>
                {selectedPromotions.length > 0 && (
                    <span style={{
                        background: 'var(--success-100)',
                        color: 'var(--success-700)',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 500
                    }}>
                        {selectedPromotions.length} selected
                    </span>
                )}
            </div>

            {availablePromotions.length === 0 ? (
                <div style={{
                    padding: 'var(--space-4)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center'
                }}>
                    <Tag size={24} style={{ color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-2)' }} />
                    <p style={{
                        margin: 0,
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--color-text-secondary)'
                    }}>
                        No promotions scheduled for this date range.
                        <br />
                        <span style={{ fontSize: 'var(--font-size-xs)' }}>
                            Content will not include any discount or sale messaging.
                        </span>
                    </p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {availablePromotions.map((promotion) => {
                        const isSelected = selectedPromotions.some(p => p.id === promotion.id);
                        const status = getPromotionStatus(promotion);
                        const Icon = PROMOTION_TYPE_ICONS[promotion.type] || Tag;
                        const overlapInfo = getOverlapInfo(promotion);

                        return (
                            <div
                                key={promotion.id}
                                onClick={() => handleTogglePromotion(promotion)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-3)',
                                    padding: 'var(--space-3)',
                                    background: isSelected ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
                                    color: isSelected ? 'white' : 'inherit',
                                    borderRadius: 'var(--radius-md)',
                                    cursor: 'pointer',
                                    border: `2px solid ${isSelected ? 'var(--color-primary)' : 'transparent'}`,
                                    transition: 'all var(--transition-fast)'
                                }}
                            >
                                <div style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: 'var(--radius-sm)',
                                    border: `2px solid ${isSelected ? 'white' : 'var(--color-border)'}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                    background: isSelected ? 'white' : 'transparent'
                                }}>
                                    {isSelected && <Check size={14} style={{ color: 'var(--color-primary)' }} />}
                                </div>

                                <div style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: 'var(--radius-md)',
                                    background: isSelected ? 'rgba(255,255,255,0.2)' : 'var(--color-bg-primary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <Icon size={16} style={{ color: isSelected ? 'white' : status.color }} />
                                </div>

                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontWeight: 500,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)'
                                    }}>
                                        {promotion.name}
                                        {promotion.isOngoing && (
                                            <span style={{
                                                fontSize: 'var(--font-size-xs)',
                                                padding: '1px 6px',
                                                borderRadius: 'var(--radius-full)',
                                                background: isSelected ? 'rgba(255,255,255,0.2)' : 'var(--success-100)',
                                                color: isSelected ? 'white' : 'var(--success-700)',
                                                fontWeight: 500,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '2px'
                                            }}>
                                                <Infinity size={10} />
                                                Ongoing
                                            </span>
                                        )}
                                    </div>
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        opacity: isSelected ? 0.8 : 0.7,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)'
                                    }}>
                                        {promotion.isOngoing ? <Infinity size={12} /> : <Calendar size={12} />}
                                        {formatDateRange(promotion)}
                                        {!overlapInfo.isOngoing && (
                                            <>
                                                <span style={{ margin: '0 4px' }}>|</span>
                                                {overlapInfo.overlapDays} days overlap
                                            </>
                                        )}
                                        {overlapInfo.isOngoing && overlapInfo.overlapDays && (
                                            <>
                                                <span style={{ margin: '0 4px' }}>|</span>
                                                Always active
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div style={{
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    color: isSelected ? 'white' : 'var(--color-primary)'
                                }}>
                                    {formatDiscountValue(promotion)}
                                </div>

                                {promotion.discountCode && (
                                    <div style={{
                                        padding: '2px 8px',
                                        background: isSelected ? 'rgba(255,255,255,0.2)' : 'var(--color-bg-primary)',
                                        borderRadius: 'var(--radius-sm)',
                                        fontFamily: 'monospace',
                                        fontSize: 'var(--font-size-xs)',
                                        border: `1px dashed ${isSelected ? 'rgba(255,255,255,0.4)' : 'var(--color-border)'}`
                                    }}>
                                        {promotion.discountCode}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Info Note */}
            <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 'var(--space-2)',
                padding: 'var(--space-3)',
                background: selectedPromotions.length > 0 ? 'var(--success-50)' : 'var(--warning-50)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-xs)',
                marginTop: 'var(--space-3)',
                border: `1px solid ${selectedPromotions.length > 0 ? 'var(--success-200)' : 'var(--warning-200)'}`
            }}>
                {selectedPromotions.length > 0 ? (
                    <>
                        <Info size={14} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--success-600)' }} />
                        <span style={{ color: 'var(--success-700)' }}>
                            Selected promotions will be featured in generated content briefs with specific discount details, promo codes, and urgency messaging.
                        </span>
                    </>
                ) : (
                    <>
                        <AlertCircle size={14} style={{ flexShrink: 0, marginTop: '2px', color: 'var(--warning-600)' }} />
                        <span style={{ color: 'var(--warning-700)' }}>
                            Without selecting a promotion, generated content will <strong>never</strong> include sales, discounts, or promotional messaging.
                        </span>
                    </>
                )}
            </div>
        </div>
    );
};

export default PromotionSelector;
